<template>
  <b-overlay
    :show="show"
    variant="transparent"
    opacity="0.99"
    blur="5px"
    rounded="sm"
  >
    <template #overlay>
      <div class="text-center">
        <b-spinner
          variant="primary"
          label="Loading"
        />
        <p id="cancel-label">
          กรุณารอสักครู่...
        </p>
      </div>
    </template>

    <b-card class="mb-1">
      <div class="d-flex justify-content-between align-items-start flex-wrap">
        <div>
          <h3 class="mb-50">
            โยกเงินออก Ezrich
          </h3>
          <p class="text-muted mb-0">
            สร้างรายการถอนจาก Payment Ezrich โดยแอดมิน รายการนี้จะแยกจากรายการถอนของสมาชิก
          </p>
        </div>
        <b-button
          variant="gradient-primary"
          class="mt-1 mt-md-0"
          @click="openCreateModal"
        >
          สร้างรายการถอน Ezrich
        </b-button>
      </div>
    </b-card>

    <b-card class="mb-1">
      <h4 class="text-dark mb-1">
        <i class="fa fa-search" /> ค้นหา
      </h4>
      <div class="row">
        <div class="col-md-2">
          <b-form-group label="วันที่เริ่มต้น">
            <flat-pickr
              v-model="dateStart"
              class="form-control"
              placeholder="เลือกวันที่เริ่มต้น"
            />
          </b-form-group>
        </div>
        <div class="col-md-2">
          <b-form-group label="วันที่สิ้นสุด">
            <flat-pickr
              v-model="dateEnd"
              class="form-control"
              placeholder="เลือกวันที่สิ้นสุด"
            />
          </b-form-group>
        </div>
        <div class="col-md-3">
          <b-form-group label="สถานะ">
            <b-form-select
              v-model="status"
              :options="statusOptions"
            />
          </b-form-group>
        </div>
        <div class="col-md-3">
          <b-form-group label="Keyword">
            <b-form-input
              v-model="search_val"
              placeholder="Order ID / เลขบัญชี / ชื่อบัญชี / หมายเหตุ"
              @keyup.enter="searchHistory"
            />
          </b-form-group>
        </div>
        <div class="col-md-2 d-flex align-items-end">
          <b-button
            variant="gradient-dark"
            class="mb-1"
            @click="searchHistory"
          >
            ค้นหา
          </b-button>
        </div>
      </div>
    </b-card>

    <b-card no-body>
      <b-table
        small
        striped
        hover
        responsive
        show-empty
        class="position-relative items-center"
        :items="items"
        :fields="fields"
        :per-page="perPage"
      >
        <template #cell(index)="data">
          {{ perPage * (currentPage - 1) + (data.index + 1) }}
        </template>

        <template #empty="scope">
          <div class="p-2 text-center">
            <img
              src="/box.png"
              alt="empty"
              height="40px"
            >
            {{ scope.emptyFiltered ? 'No matching records found' : 'ไม่พบข้อมูล' }}
          </div>
        </template>

        <template #cell(created_at)="data">
          {{ formatDate(data.item.created_at) }}
        </template>

        <template #cell(account)="data">
          <div>
            <div class="font-weight-bolder">
              {{ data.item.account_name || '-' }}
            </div>
            <small class="text-info">{{ data.item.account_number || '-' }}</small>
          </div>
        </template>

        <template #cell(bank)="data">
          <div>
            <div class="font-weight-bolder">
              {{ data.item.bank_name || '-' }}
            </div>
            <small class="text-muted">{{ data.item.ezrich_bank_code || data.item.bank_code || '-' }}</small>
          </div>
        </template>

        <template #cell(amount)="data">
          <span class="font-weight-bolder text-danger">
            {{ Commas(Number(data.item.amount || 0).toFixed(2)) }}
          </span>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="getStatusVariant(data.item.status)">
            {{ data.item.status || '-' }}
          </b-badge>
        </template>

        <template #cell(note)="data">
          <span>{{ data.item.note || '-' }}</span>
        </template>
      </b-table>

      <b-card-body class="d-flex justify-content-between flex-wrap align-items-center">
        <div class="text-muted mb-1 mb-md-0">
          ทั้งหมด {{ totalRows }} รายการ
        </div>
        <div
          v-if="totalRows > perPage"
          class="d-flex align-items-center"
        >
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="currentPage <= 1"
            class="mr-50"
            @click="changePage(currentPage - 1)"
          >
            ก่อนหน้า
          </b-button>
          <span class="text-muted mx-50">
            หน้า {{ currentPage }} / {{ totalPages }}
          </span>
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="currentPage >= totalPages"
            class="ml-50"
            @click="changePage(currentPage + 1)"
          >
            ถัดไป
          </b-button>
        </div>
      </b-card-body>
    </b-card>

    <b-modal
      ref="create-modal"
      title="สร้างรายการถอน Ezrich"
      ok-title="สร้างรายการ"
      cancel-title="ยกเลิก"
      @ok.prevent="confirmCreate"
      @hidden="resetForm"
    >
      <b-form-group label="จำนวนเงิน">
        <b-form-input
          v-model="form.amount"
          type="number"
          min="1"
          step="0.01"
          placeholder="กรอกจำนวนเงินที่ต้องการถอน"
        />
      </b-form-group>

      <b-form-group label="ธนาคารปลายทาง">
        <b-form-select
          v-model="form.bank_code"
          :options="bankOptions"
        />
      </b-form-group>

      <b-form-group label="เลขบัญชี">
        <b-form-input
          v-model="form.account_number"
          placeholder="กรอกเลขบัญชีปลายทาง"
        />
      </b-form-group>

      <b-form-group label="ชื่อบัญชี">
        <b-form-input
          v-model="form.account_name"
          placeholder="กรอกชื่อบัญชีปลายทาง"
        />
      </b-form-group>

      <b-form-group label="หมายเหตุ">
        <b-form-textarea
          v-model="form.note"
          rows="3"
          placeholder="หมายเหตุเพิ่มเติม"
        />
      </b-form-group>
    </b-modal>
  </b-overlay>
</template>

<script>
import moment from 'moment-timezone'
import flatPickr from 'vue-flatpickr-component'
import {
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BModal,
  BOverlay,
  BSpinner,
  BTable,
} from 'bootstrap-vue'

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BModal,
    BOverlay,
    BSpinner,
    BTable,
    flatPickr,
  },
  data() {
    return {
      show: false,
      items: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      dateStart: moment().tz('Asia/Bangkok').format('YYYY-MM-DD'),
      dateEnd: moment().tz('Asia/Bangkok').format('YYYY-MM-DD'),
      status: null,
      search_val: null,
      form: {
        amount: null,
        bank_code: null,
        account_number: '',
        account_name: '',
        note: '',
      },
      fields: [
        { key: 'index', label: 'ลำดับ' },
        { key: 'created_at', label: 'วันที่สร้าง' },
        { key: 'customer_order_id', label: 'Order ID' },
        { key: 'bank', label: 'ธนาคาร' },
        { key: 'account', label: 'บัญชีปลายทาง' },
        {
          key: 'amount',
          label: 'จำนวนเงิน',
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'status',
          label: 'สถานะ',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        { key: 'created_by', label: 'ผู้ทำรายการ' },
        { key: 'note', label: 'หมายเหตุ' },
      ],
      statusOptions: [
        { value: null, text: 'ทั้งหมด' },
        { value: 'processing', text: 'processing' },
        { value: 'success', text: 'success' },
        { value: 'error', text: 'error' },
        { value: 'cancelled', text: 'cancelled' },
      ],
      bankOptions: [
        { value: null, text: 'เลือกธนาคารปลายทาง' },
        { value: '004', text: 'ธนาคารกสิกรไทย (KBANK)' },
        { value: '002', text: 'ธนาคารกรุงเทพ (BBL)' },
        { value: '006', text: 'ธนาคารกรุงไทย (KTB)' },
        { value: '011', text: 'ธนาคารทหารไทยธนชาต (TTB)' },
        { value: '014', text: 'ธนาคารไทยพาณิชย์ (SCB)' },
        { value: '017', text: 'ซิตี้แบงก์ (CITI)' },
        { value: '018', text: 'ธนาคารซูมิโตโม มิตซุย (SMBC)' },
        { value: '020', text: 'ธนาคารสแตนดาร์ดชาร์เตอร์ด (SCBT)' },
        { value: '022', text: 'ธนาคารซีไอเอ็มบีไทย (CIMB)' },
        { value: '024', text: 'ธนาคารยูโอบี (UOB)' },
        { value: '025', text: 'ธนาคารกรุงศรีอยุธยา (BAY)' },
        { value: '030', text: 'ธนาคารออมสิน (GSB)' },
        { value: '033', text: 'ธนาคารอาคารสงเคราะห์ (GHB)' },
        { value: '034', text: 'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร (BAAC)' },
        { value: '066', text: 'ธนาคารอิสลามแห่งประเทศไทย (ISBT)' },
        { value: '067', text: 'ธนาคารทิสโก้ (TSCO)' },
        { value: '069', text: 'ธนาคารเกียรตินาคินภัทร (KK)' },
        { value: '070', text: 'ธนาคารไอซีบีซี (ICBC)' },
        { value: '071', text: 'ธนาคารไทยเครดิตเพื่อรายย่อย (TCRB)' },
        { value: '073', text: 'ธนาคารแลนด์ แอนด์ เฮ้าส์ (LHB)' },
        { value: '016', text: 'ธนาคารเพื่อการส่งออกและนำเข้าแห่งประเทศไทย (EXIM)' },
        { value: '015', text: 'ธนาคารพัฒนาวิสาหกิจขนาดกลางและขนาดย่อม (SME)' },
      ],
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalRows / this.perPage))
    },
  },
  mounted() {
    this.getHistory()
  },
  methods: {
    getHistory() {
      this.show = true
      const params = {
        page: this.currentPage,
        perPage: this.perPage,
        status: this.status,
        search_val: this.search_val,
        dateStart: this.dateStart,
        dateEnd: this.dateEnd,
      }
      return this.$http
        .get('/ezrich/withdrawals', { params })
        .then(response => {
          const res = response.data
          this.items = res.data || []
          this.totalRows = res.total || 0
        })
        .catch(error => {
          const message = error.response && error.response.data ? error.response.data.message : 'ไม่สามารถดึงข้อมูลได้'
          this.$swal('เกิดข้อผิดพลาด', message, 'error')
        })
        .finally(() => {
          this.show = false
        })
    },
    searchHistory() {
      this.currentPage = 1
      return this.getHistory()
    },
    changePage(page) {
      this.currentPage = page
      return this.getHistory()
    },
    openCreateModal() {
      this.$refs['create-modal'].show()
    },
    validateForm() {
      if (!this.form.amount || Number(this.form.amount) <= 0) {
        this.$swal('แจ้งเตือน', 'กรุณากรอกจำนวนเงินให้ถูกต้อง', 'warning')
        return false
      }
      if (!this.form.bank_code) {
        this.$swal('แจ้งเตือน', 'กรุณาเลือกธนาคารปลายทาง', 'warning')
        return false
      }
      if (!this.form.account_number) {
        this.$swal('แจ้งเตือน', 'กรุณากรอกเลขบัญชีปลายทาง', 'warning')
        return false
      }
      if (!this.form.account_name) {
        this.$swal('แจ้งเตือน', 'กรุณากรอกชื่อบัญชีปลายทาง', 'warning')
        return false
      }
      return true
    },
    confirmCreate() {
      if (!this.validateForm()) return

      const bank = this.bankOptions.find(item => item.value === this.form.bank_code)
      this.$swal({
        title: 'ยืนยันสร้างรายการถอน?',
        html: `
          <div class="text-left">
            <p><strong>จำนวนเงิน:</strong> ${this.Commas(Number(this.form.amount).toFixed(2))} บาท</p>
            <p><strong>ธนาคาร:</strong> ${bank ? bank.text : '-'}</p>
            <p><strong>เลขบัญชี:</strong> ${this.form.account_number}</p>
            <p><strong>ชื่อบัญชี:</strong> ${this.form.account_name}</p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.isConfirmed) {
          this.createWithdraw()
        }
      })
    },
    createWithdraw() {
      this.show = true
      const payload = {
        amount: Number(this.form.amount),
        bank_code: this.form.bank_code,
        account_number: this.form.account_number,
        account_name: this.form.account_name,
        note: this.form.note,
      }
      this.$http
        .post('/ezrich/withdraw', payload)
        .then(() => {
          this.$refs['create-modal'].hide()
          this.$swal('สำเร็จ', 'สร้างรายการถอน Ezrich สำเร็จ', 'success')
          return this.searchHistory()
        })
        .catch(error => {
          const message = error.response && error.response.data ? error.response.data.message : 'ไม่สามารถสร้างรายการได้'
          this.$swal('เกิดข้อผิดพลาด', message, 'error')
        })
        .finally(() => {
          this.show = false
        })
    },
    resetForm() {
      this.form = {
        amount: null,
        bank_code: null,
        account_number: '',
        account_name: '',
        note: '',
      }
    },
    formatDate(value) {
      if (!value) return '-'
      return moment(value).tz('Asia/Bangkok').format('DD/MM/YYYY HH:mm:ss')
    },
    getStatusVariant(status) {
      if (status === 'success') return 'light-success'
      if (status === 'processing') return 'light-warning'
      if (status === 'cancelled') return 'light-secondary'
      if (status === 'error') return 'light-danger'
      return 'light-primary'
    },
    Commas(x) {
      if (!x) {
        return 0
      }
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
